import { useState } from "react";
import { ChevronDown } from "lucide-react";
import Card from "@imminently/decisively-ui/Card";
import Checkbox from "@imminently/decisively-ui/Checkbox";
import Chip from "@imminently/decisively-ui/Chip";

import { type AdapterExternalProduct, fillAnswerTemplates, getExternalConfigConfirmationId } from "@/features/adapters/model";
import type { AdapterBlockProps } from "@/features/adapters/stepTypes";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/ui/primitives/collapsible";
import { Separator } from "@/ui/primitives/separator";
import { AdapterContent, AdapterMarkdown } from "../AdapterContent";

/** Markdown shown on a step, with any answered `{{answer.key}}` filled in. */
export function ContentBlock({ block, shell }: AdapterBlockProps<"content">) {
  return <AdapterContent content={fillAnswerTemplates(block.content, shell.answers, { keepUnanswered: true })} />;
}

/** Separates the blocks before it from those after. */
export function DividerBlock() {
  return <Separator data-adapter-divider className="my-3" />;
}

const productLogos: Record<AdapterExternalProduct, { src: string; alt: string }> = {
  edward: { src: "/images/edward-logo.svg", alt: "Edward" },
};

/**
 * Setup the step needs in another product. The "Done" checkbox counts toward step completion only when the
 * block is required; otherwise it's a note for the person configuring the adapter.
 */
export function ExternalConfigBlock({ step, block, shell }: AdapterBlockProps<"externalConfig">) {
  const confirmationId = getExternalConfigConfirmationId(step.key, block.id);
  const done = Boolean(shell.confirmations[confirmationId]);
  const [open, setOpen] = useState(!done);
  const logo = productLogos[block.product];
  const checkboxId = `adapter-external-config-${confirmationId}`;
  return <Card className="gap-0 py-0" data-adapter-external-config={block.id}>
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 px-3 py-3 text-left"
          aria-label={`${open ? "Collapse" : "Expand"} ${block.title}`}
        >
          <img src={logo.src} alt={logo.alt} className="h-6 w-6 shrink-0" />
          <span className="flex-1 text-sm font-semibold text-foreground">{block.title}</span>
          {done ? <Chip variant="success" size="sm" label="Done" /> : null}
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Card.Content className="grid gap-3 px-3 pb-3">
          <AdapterMarkdown content={block.content} compact />
          <div className="flex items-center gap-2">
            <Checkbox
              id={checkboxId}
              checked={done}
              disabled={shell.busy}
              onCheckedChange={(checked) => shell.setConfirmation(confirmationId, checked === true)}
            />
            <label className="text-sm text-foreground" htmlFor={checkboxId}>Done</label>
            <span className="text-xs text-muted-foreground">
              {block.required ? "Required to complete this step." : "Optional. Not required to complete this step."}
            </span>
          </div>
        </Card.Content>
      </CollapsibleContent>
    </Collapsible>
  </Card>;
}
